import React from 'react';
import { toast } from 'sonner';
import { apiService } from '../../utils/api';
import { getFieldErrors } from '../../utils/apiError';
import { useParams } from 'react-router-dom';
import ResetPasswordConfirmForm from './ResetPasswordConfirmForm';
import history from '../../history';
import Logo from '../../images/logoMoiry.svg';
import { KeyRound, CheckCircle } from 'lucide-react';

const ResetPasswordConfirm = () => {
  const { token } = useParams<{ token: string }>();

  const submitNewPass = (values, setErrors) => {
    delete apiService.defaults.headers.common.Authorization;

    apiService
      .post('/my-rest-auth/password-reset-complete/', {
        password: values.password,
        token,
      })
      .then(() => {
        toast.success('Heslo úspěšně změněno.');
        history.push('/login');
      })
      .catch((error) => {
        setErrors(getFieldErrors(error));
      });
  };

  return (
    <div className="min-h-screen bg-neutral-50 relative overflow-hidden">
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(var(--neutral-100) 1px, transparent 1px), linear-gradient(90deg, var(--neutral-100) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-neutral-50 pointer-events-none" />

      <div className="relative z-10 grid lg:grid-cols-[1fr_1.1fr] min-h-screen">
        {/* Left: form */}
        <div className="flex flex-col px-12 py-10">
          <div className="flex items-center">
            <img alt="MOIRY" src={Logo} className="h-6" />
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-sm">
              <div className="h-12 w-12 rounded-xl bg-primary-50 flex items-center justify-center mb-6">
                <KeyRound size={20} className="text-primary-600" />
              </div>

              <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
                Zvolte nové heslo
              </h1>
              <p className="text-sm text-neutral-500 mt-2">
                Zadejte nové heslo pro váš účet. Heslo musí splňovat bezpečnostní požadavky.
              </p>

              <div className="mt-8">
                <ResetPasswordConfirmForm submit={submitNewPass as any} />
              </div>
            </div>
          </div>

          <div className="text-xs text-neutral-400 flex items-center justify-between">
            <span>&copy; 2026 MOIRY</span>
          </div>
        </div>

        {/* Right: security info panel */}
        <div className="relative hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(var(--neutral-100) 1px, transparent 1px), linear-gradient(90deg, var(--neutral-100) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />

          <div className="absolute bottom-0 right-0 opacity-[0.06] pointer-events-none">
            <img alt="" src={Logo} className="h-80 brightness-0 invert" />
          </div>

          <div className="relative z-10 flex flex-col h-full">
            <div />

            <div className="flex-1 flex items-center">
              <div className="max-w-lg">
                <div className="text-[11px] uppercase tracking-[0.2em] text-primary-300 font-medium mb-4">
                  Zabezpečení účtu
                </div>
                <h2 className="text-4xl font-semibold tracking-tight leading-[1.1]">
                  Bezpečný přístup k vaší{' '}
                  <span className="text-primary-400">projektové dokumentaci</span>.
                </h2>
                <p className="text-base text-neutral-300 mt-5 leading-relaxed">
                  Odkaz pro reset hesla je platný 30 minut a lze jej použít jen jednou. Vaše data
                  zůstávají v bezpečí.
                </p>

                <div className="mt-10 space-y-6">
                  {[
                    {
                      title: 'Šifrovaný přenos',
                      desc: 'TLS 1.3 a end-to-end ochrana přihlašovacích údajů.',
                    },
                    {
                      title: 'Jednorázový odkaz',
                      desc: 'Platnost 30 min, po použití okamžitě zneplatněn.',
                    },
                    {
                      title: 'Audit přihlášení',
                      desc: 'Každý reset je zaznamenán v historii účtu.',
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-primary-400 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium text-white">{item.title}</div>
                        <div className="text-sm text-neutral-400 mt-0.5">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative z-10 border-t border-white/10 pt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3 text-neutral-400">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span>Všechny systémy online</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
